// HeroSection.jsx
import React, { useEffect, useRef } from "react";
import { Box, Button, Stack, alpha, Container, Typography, Chip, keyframes } from "@mui/material";
import { motion } from "framer-motion";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarsIcon from '@mui/icons-material/Stars';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import DiamondIcon from '@mui/icons-material/Diamond';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Link } from "react-router-dom";
import WorldMap from '../UI/WorldMap';

// Premium animations
const goldShimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const float = keyframes`
  0% {
    transform: translateY(0px) rotate(0deg);
  }
  50% {
    transform: translateY(-6px) rotate(1deg);
  }
  100% {
    transform: translateY(0px) rotate(0deg);
  }
`;

const pulseRing = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.4);
    opacity: 1;
  }
  50% {
    box-shadow: 0 0 0 12px rgba(212, 175, 55, 0);
    opacity: 0.8;
  }
  100% {
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0);
    opacity: 1;
  }
`;

const glowText = keyframes`
  0% {
    text-shadow: 0 0 5px rgba(212, 175, 55, 0.3);
  }
  50% {
    text-shadow: 0 0 20px rgba(212, 175, 55, 0.8), 0 0 30px rgba(255, 215, 0, 0.4);
  }
  100% {
    text-shadow: 0 0 5px rgba(212, 175, 55, 0.3);
  }
`;

const shimmerDiagonal = keyframes`
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
`;

const rotateSlow = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const premiumGlow = keyframes`
  0% {
    border-color: rgba(212, 175, 55, 0.3);
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.2);
  }
  50% {
    border-color: rgba(212, 175, 55, 0.8);
    box-shadow: 0 0 20px 5px rgba(212, 175, 55, 0.4);
  }
  100% {
    border-color: rgba(212, 175, 55, 0.3);
    box-shadow: 0 0 0 0 rgba(212, 175, 55, 0.2);
  }
`;

const sparkle = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
`;

const HeroSection = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    const imagePaths = [
      '/conticoins/Europe (Austria) – Philharmonic.jpg',
      '/conticoins/Europe (UK) – Britannia (your 3144 image likely this).jpg',
      '/conticoins/Asia – Panda.jpg',
      '/conticoins/Asia – Panda2.jpg',
      '/conticoins/North America – American Eagle.jpg',
      '/conticoins/Australian Gold Kangaroo.jpg',
      '/conticoins/North America.jpg',
    ];
    imagePaths.forEach(path => {
      const img = new Image();
      img.src = path;
    });
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        margin: 0,
        padding: 0,
        background: 'linear-gradient(135deg, #000033 0%, #0A0A3A 50%, #000022 100%)',
      }}
    >

      {/* Full-width World Map */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          height: 'calc(100% - 80px)',
          zIndex: 1,
        }}
      >
        <WorldMap />
      </Box>

      {/* PREMIUM 25th Anniversary Celebration Badge */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, x: -50, scale: 0.9 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2, type: "spring", stiffness: 300 }}
        sx={{
          position: 'absolute',
          top: { xs: 20, sm: 24, md: 30 },
          left: { xs: 20, sm: 24, md: 30 },
          zIndex: 15,
          cursor: 'pointer',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            animation: `${float} 6s ease-in-out infinite`,
          }}
        >
          {/* Premium Outer Rings */}
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              left: -20,
              right: -20,
              bottom: -20,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0) 70%)',
              animation: `${premiumGlow} 3s ease-in-out infinite`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: -15,
              left: -15,
              right: -15,
              bottom: -15,
              borderRadius: '50%',
              border: '1px solid rgba(212, 175, 55, 0.3)',
              animation: `${pulseRing} 2.5s ease-in-out infinite`,
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: -8,
              left: -8,
              right: -8,
              bottom: -8,
              borderRadius: '50%',
              border: '0.5px solid rgba(212, 175, 55, 0.15)',
            }}
          />
          
          {/* Main Badge - Enhanced Premium Design */}
          <Box
            sx={{
              position: 'relative',
              width: { xs: 220, sm: 260, md: 280 },
              background: 'linear-gradient(165deg, rgba(5, 5, 20, 0.98) 0%, rgba(15, 15, 50, 0.98) 100%)',
              backdropFilter: 'blur(12px)',
              borderRadius: 4,
              border: '1.5px solid rgba(212, 175, 55, 0.4)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(212,175,55,0.1) inset, 0 0 20px rgba(212,175,55,0.1)',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #FFF8E1, #FFD700, #D4AF37, transparent)',
                animation: `${goldShimmer} 2.5s linear infinite`,
              },
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(212,175,55,0.3), transparent)',
              },
            }}
          >
            {/* Enhanced Shimmer Effect */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(45deg, transparent, rgba(212,175,55,0.08), transparent)',
                transform: 'translateX(-100%) translateY(-100%) rotate(45deg)',
                animation: `${shimmerDiagonal} 6s ease-in-out infinite`,
                pointerEvents: 'none',
              }}
            />
            
            {/* Sparkle Effects */}
            {[...Array(6)].map((_, i) => (
              <Box
                key={i}
                component={motion.div}
                animate={{
                  opacity: [0, 1, 0],
                  scale: [0, 1.5, 0],
                }}
                transition={{
                  duration: 2,
                  delay: i * 0.5,
                  repeat: Infinity,
                  repeatDelay: 3,
                }}
                sx={{
                  position: 'absolute',
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  width: 4,
                  height: 4,
                  background: '#FFD700',
                  borderRadius: '50%',
                  boxShadow: '0 0 10px #FFD700',
                  pointerEvents: 'none',
                }}
              />
            ))}
            
            {/* Content */}
            <Box sx={{ p: { xs: 2.5, sm: 3, md: 3.5 }, textAlign: 'center' }}>
              
              {/* Rotating Diamond with Premium Ring */}
              <Box
                sx={{
                  position: 'relative',
                  display: 'inline-block',
                  mb: 1,
                }}
              >
                <Box
                  component={motion.div}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                  sx={{
                    position: 'absolute',
                    top: -8,
                    left: -8,
                    right: -8,
                    bottom: -8,
                    borderRadius: '50%',
                    border: '1px solid rgba(212,175,55,0.3)',
                  }}
                />
                <Box
                  component={motion.div}
                  animate={{ rotate: -360 }}
                  transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <DiamondIcon sx={{ color: '#D4AF37', fontSize: { xs: 32, sm: 36, md: 40 } }} />
                </Box>
              </Box>
              
              {/* Premium Celebrating Badge */}
              <Chip
                label="PREMIER CELEBRATION"
                icon={<WorkspacePremiumIcon sx={{ fontSize: 12 }} />}
                size="small"
                sx={{
                  bgcolor: alpha('#D4AF37', 0.15),
                  color: '#D4AF37',
                  fontSize: '0.55rem',
                  height: 20,
                  fontWeight: 700,
                  letterSpacing: 1.5,
                  mb: 1,
                  borderRadius: 2,
                  border: '0.5px solid rgba(212,175,55,0.3)',
                  '& .MuiChip-icon': { fontSize: 12, color: '#D4AF37' }
                }}
              />
              
              {/* 25 Years - Premium Typography */}
              <Typography
                sx={{
                  fontSize: { xs: '2rem', sm: '2.2rem', md: '2.4rem' },
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, #D4AF37 0%, #FFE44D 30%, #FFF8E1 50%, #FFD700 70%, #DAA520 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  lineHeight: 1,
                  animation: `${glowText} 3s ease-in-out infinite`,
                  letterSpacing: '-0.02em',
                  mb: 0.5,
                  fontFamily: '"Playfair Display", "Georgia", serif',
                }}
              >
                25 Years
              </Typography>
              
              <Typography
                sx={{
                  fontSize: { xs: '0.65rem', sm: '0.7rem', md: '0.75rem' },
                  letterSpacing: 2,
                  color: alpha('#FFFFFF', 0.7),
                  fontWeight: 600,
                  mb: 1.5,
                  textTransform: 'uppercase',
                }}
              >
                of Excellence & Innovation
              </Typography>
              
              {/* Premium Divider */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 30, height: 1.5, background: 'linear-gradient(90deg, #D4AF37, transparent)' }} />
                <DiamondIcon sx={{ color: '#D4AF37', fontSize: 10 }} />
                <Box sx={{ width: 30, height: 1.5, background: 'linear-gradient(90deg, transparent, #D4AF37)' }} />
              </Box>
              
              {/* Premium Stats Row */}
              <Stack
                direction="row"
                spacing={1.5}
                justifyContent="center"
                alignItems="center"
                sx={{ mb: 1.5, background: alpha('#D4AF37', 0.05), borderRadius: 2, p: 1 }}
              >
               
              
               
              </Stack>
              
              {/* Premium Trust Badge */}
              <Chip
                label="⭐ TRUSTED GLOBAL PARTNER SINCE 1999 ⭐"
                size="small"
                sx={{
                  bgcolor: alpha('#FFFFFF', 0.05),
                  color: alpha('#FFD700', 0.7),
                  fontSize: '0.5rem',
                  height: 18,
                  fontWeight: 600,
                  letterSpacing: 1,
                  borderRadius: 2,
                }}
              />
            </Box>
          </Box>
          
          {/* Premium Corner Accents */}
          <Box
            component={motion.div}
            animate={{ rotate: 360 }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            sx={{
              position: 'absolute',
              top: -15,
              left: -15,
              width: 20,
              height: 20,
              background: 'linear-gradient(135deg, #D4AF37, #FFD700)',
              clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
              opacity: 0.6,
              filter: 'blur(0.5px)',
            }}
          />
          <Box
            component={motion.div}
            animate={{ rotate: -360 }}
            transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
            sx={{
              position: 'absolute',
              bottom: -15,
              right: -15,
              width: 20,
              height: 20,
              background: 'linear-gradient(135deg, #FFD700, #D4AF37)',
              clipPath: 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)',
              opacity: 0.6,
              filter: 'blur(0.5px)',
            }}
          />
        </Box>
      </Box>

      {/* Bottom Left Brand Text */}
      <Container
        maxWidth="xl"
        sx={{
          position: 'absolute',
          bottom: 120,
          left: 0,
          right: 0,
          zIndex: 2,
          pointerEvents: 'none',
        }}
      >
        <Box sx={{ maxWidth: 500, pl: { xs: 3, sm: 4, md: 5 } }}>
          <Typography
            variant="overline"
            sx={{
              color: alpha('#FFFFFF', 0.3),
              letterSpacing: 5,
              fontWeight: 600,
              fontSize: '0.6rem',
            }}
          >
            GLOBAL PRECIOUS METALS LEADER
          </Typography>
        </Box>
      </Container>

      {/* Button Container */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '80px',
          zIndex: 3,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: alpha('#000033', 0.95),
          borderTop: '1px solid rgba(212, 175, 55, 0.2)',
          backdropFilter: 'blur(10px)',
        }}
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={3}
          sx={{ 
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <Button 
              variant="contained" 
              size="large"
              component={Link}
              to="/contact"
              endIcon={<ArrowForwardIcon />}
              sx={{ 
                px: { xs: 4, md: 5 }, 
                py: { xs: 1, md: 1.2 },
                fontSize: { xs: '0.85rem', md: '0.9rem' },
                fontWeight: 700,
                letterSpacing: '0.05em',
                background: 'linear-gradient(135deg, #D4AF37 0%, #FFE44D 30%, #FFD700 70%, #DAA520 100%)',
                color: '#000033',
                boxShadow: '0 10px 30px -10px rgba(212, 175, 55, 0.5)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FFE44D 0%, #FFD700 50%, #D4AF37 100%)',
                  boxShadow: '0 15px 40px -10px rgba(212, 175, 55, 0.8)',
                },
              }}
            >
              Start Investing
            </Button>
          </motion.div>
          
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <Button 
              variant="outlined" 
              size="large"
              component={Link}
              to="/market-data"
              startIcon={<StarsIcon />}
              sx={{ 
                px: { xs: 4, md: 5 }, 
                py: { xs: 1, md: 1.2 },
                fontSize: { xs: '0.85rem', md: '0.9rem' },
                fontWeight: 700,
                letterSpacing: '0.05em',
                borderColor: '#D4AF37',
                color: '#D4AF37',
                borderWidth: '2px',
                '&:hover': {
                  borderColor: '#FFD700',
                  backgroundColor: alpha('#D4AF37', 0.1),
                  boxShadow: '0 0 20px rgba(212,175,55,0.4)',
                },
              }}
            >
              Live Market Data
            </Button>
          </motion.div>
        </Stack>
      </Box>

      {/* Scroll Indicator */}
      <Box
        component={motion.div}
        animate={{ y: [0, 5, 0] }}
        transition={{ duration: 1.5, repeat: Infinity }}
        sx={{
          position: 'absolute',
          bottom: 100,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          cursor: 'pointer',
        }}
        onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
      >
        <Stack alignItems="center" spacing={0.5}>
          <Box
            sx={{
              width: 20,
              height: 32,
              border: '1.5px solid',
              borderColor: alpha('#D4AF37', 0.4),
              borderRadius: 16,
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 5,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 2.5,
                height: 6,
                backgroundColor: '#D4AF37',
                borderRadius: 2,
                animation: 'scrollIndicator 1.5s infinite',
              }
            }}
          />
          <Typography variant="caption" sx={{ color: alpha('#D4AF37', 0.5), fontSize: '0.6rem', letterSpacing: 1 }}>
            SCROLL
          </Typography>
        </Stack>
      </Box>

      <style>
        {`
          @keyframes scrollIndicator {
            0% { opacity: 1; transform: translateX(-50%) translateY(0); }
            100% { opacity: 0; transform: translateX(-50%) translateY(10px); }
          }
        `}
      </style>
    </Box>
  );
};

export default HeroSection;